import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import LoginPage from './pages/LoginPage';
import About from './pages/About';
import Rules from './pages/Rules';
import Prices from './pages/Prices';
import Dashboard from './pages/Dashboard';
import PublicProfile from './pages/PublicProfile';
import Admin2Page from './pages/Admin2Page';
import TokenProfile from './pages/TokenProfile';
import LinkedinCallback from './pages/LinkedinCallback';
import Unsubscribe from './pages/Unsubscribe';
import Privacy from './pages/Privacy';
import JoinCommunityPage from './pages/JoinCommunityPage';
import MyCommunitiesPage from './pages/MyCommunitiesPage';
import CommunityInfoPage from './pages/CommunityInfoPage';

import './App.css';
import { SESSION_RESET_EVENT } from './api/client';

const RequireAuth = ({ user, children }) => {
    if (!user) {
        return <Navigate to="/" replace />;
    }
    return children;
};

// Defined outside App on purpose (2026-09-24): as an inner component it got a
// new identity on every App render, so any App state change — a login, a
// session reset — remounted the whole page, e.g. LoginPage in the middle of a
// bot-code verify, losing its state and error message.
const MainLayout = ({ user, onLogout, children }) => {
    const location = useLocation();
    const isFullWidth = location.pathname === '/' || location.pathname.startsWith('/profile') || location.pathname.startsWith('/community');

    // Track page views
    React.useEffect(() => {
        const username = user?.Tg_Username;
        const API = process.env.REACT_APP_API_URL || '';
        fetch(`${API}/api/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: location.pathname, username }),
        }).catch(() => {});
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="app-container">
            <div className="background-decoration">
                <div className="bg-circle bg-circle-1"></div>
                <div className="bg-circle bg-circle-2"></div>
                <div className="bg-circle bg-circle-3"></div>
            </div>

            <Header user={user} onLogout={onLogout} />

            <main className={`main-content ${isFullWidth ? 'main-content-fluid' : ''}`}>
                {children}
            </main>

            <Footer />
        </div>
    );
};

function App() {
    const [user, setUser] = React.useState(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    });

    const handleLogin = (u) => {
        localStorage.setItem('user', JSON.stringify(u));
        setUser(u);
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        setUser(null);
    };

    // A 401 on a stored session drops it (api/client.js resetSession). The
    // in-memory copy has to go too, or it gets written straight back into
    // storage and the login page keeps trusting a dead token.
    React.useEffect(() => {
        const onReset = () => setUser(null);
        window.addEventListener(SESSION_RESET_EVENT, onReset);
        return () => window.removeEventListener(SESSION_RESET_EVENT, onReset);
    }, []);

    return (
        <ErrorBoundary>
        <Router>
            <Routes>
                {/* Admin Route - Standalone */}
                <Route path="/admin" element={<Admin2Page user={user} />} />
                <Route path="/admin2" element={<Admin2Page user={user} />} />

                {/* Unsubscribe Route - Standalone (no header/footer) */}
                <Route path="/unsubscribe" element={<Unsubscribe />} />

                {/* Main App Routes - Wrapped in Layout */}
                <Route path="*" element={
                    <MainLayout user={user} onLogout={handleLogout}>
                        <Routes>
                            <Route path="/" element={user ? <Dashboard /> : <Home />} />
                            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" replace />} />
                            <Route path="/view/:token" element={<TokenProfile />} />
                            <Route path="/auth/linkedin/callback" element={<LinkedinCallback onLogin={handleLogin} />} />
                            <Route path="/login" element={
                                user && !new URLSearchParams(window.location.search).get('code')
                                    ? <Navigate to="/" replace />
                                    : <LoginPage onLogin={handleLogin} />
                            } />
                            <Route path="/join/:code" element={<JoinCommunityPage user={user} />} />
                            <Route path="/my/communities" element={
                                <RequireAuth user={user}>
                                    <MyCommunitiesPage user={user} />
                                </RequireAuth>
                            } />
                            <Route path="/community/:slug" element={
                                <RequireAuth user={user}>
                                    <CommunityInfoPage user={user} />
                                </RequireAuth>
                            } />
                            <Route path="/about" element={<About />} />
                            <Route path="/rules" element={<Rules />} />
                            <Route path="/prices" element={<Prices user={user} />} />
                            <Route path="/terms_and_privacy" element={<Privacy />} />
                            <Route path="/profile/:username" element={
                                <RequireAuth user={user}>
                                    <PublicProfile />
                                </RequireAuth>
                            } />
                            {/* Catch all for 404 inside layout if needed, or redirect */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </MainLayout>
                } />
            </Routes>
        </Router>
        </ErrorBoundary>
    );
}

export default App;

